import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { RichTextEditor } from './RichTextEditor';
import { useCreateService, useUpdateService } from '@/hooks/useServices';
import { useTranslation } from '@/features/i18n';
import { generateSlug } from '@/lib/utils';
import type { Service, Feature, Benefit, FAQ } from '@/types/database';

type ServiceFormData = {
  title: string;
  slug: string;
  short_description?: string;
  icon: string;
  hero_image_url?: string;
  cta_text?: string;
  cta_link?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  order_index: number;
  is_featured: boolean;
  status: 'draft' | 'published';
};

interface ServiceEditorProps {
  service?: Service | null;
  onClose: () => void;
}

export function ServiceEditor({ service, onClose }: ServiceEditorProps) {
  const [longDescription, setLongDescription] = useState(service?.long_description || '');
  const [features, setFeatures] = useState<Feature[]>(service?.features_json || []);
  const [benefits, setBenefits] = useState<Benefit[]>(service?.benefits_json || []);
  const [faqs, setFaqs] = useState<FAQ[]>(service?.faq_json || []);

  const createService = useCreateService();
  const updateService = useUpdateService();
  const { t } = useTranslation();

  const serviceSchema = z.object({
    title: z.string().min(1, t.admin.editor.titleRequired),
    slug: z.string().min(1, t.admin.editor.slugRequired),
    short_description: z.string().optional(),
    icon: z.string(),
    hero_image_url: z.string().url().optional().or(z.literal('')),
    cta_text: z.string().optional(),
    cta_link: z.string().optional(),
    seo_title: z.string().optional(),
    seo_description: z.string().optional(),
    seo_keywords: z.string().optional(),
    order_index: z.number(),
    is_featured: z.boolean(),
    status: z.enum(['draft', 'published']),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      title: service?.title || '',
      slug: service?.slug || '',
      short_description: service?.short_description || '',
      icon: service?.icon || 'box',
      hero_image_url: service?.hero_image_url || '',
      cta_text: service?.cta_text || t.common.getStarted,
      cta_link: service?.cta_link || '/contact',
      seo_title: service?.seo_title || '',
      seo_description: service?.seo_description || '',
      seo_keywords: service?.seo_keywords || '',
      order_index: service?.order_index || 0,
      is_featured: service?.is_featured || false,
      status: service?.status || 'draft',
    },
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setValue('title', newTitle);
    if (!service) {
      setValue('slug', generateSlug(newTitle));
    }
  };

  const onSubmit = async (data: ServiceFormData) => {
    const serviceData = {
      ...data,
      long_description: longDescription,
      features_json: features,
      benefits_json: benefits,
      faq_json: faqs,
      translations: service?.translations || {},
      pricing_info: null,
      og_image_url: data.hero_image_url || null,
    };

    if (service) {
      await updateService.mutateAsync({ id: service.id, ...serviceData });
    } else {
      await createService.mutateAsync(serviceData as Parameters<typeof createService.mutateAsync>[0]);
    }
    onClose();
  };

  const iconOptions = [
    { value: 'users', label: 'Users (CRM)' },
    { value: 'smartphone', label: 'Smartphone (App)' },
    { value: 'globe', label: 'Globe (Web)' },
    { value: 'message-square', label: 'Message (SMS)' },
    { value: 'mic', label: 'Mic (Voice)' },
    { value: 'box', label: 'Box (Default)' },
    { value: 'zap', label: 'Zap' },
    { value: 'shield', label: 'Shield' },
    { value: 'bar-chart', label: 'Bar Chart' },
    { value: 'heart', label: 'Heart' },
  ];

  const statusOptions = [
    { value: 'draft', label: t.admin.common.draft },
    { value: 'published', label: t.admin.common.published },
  ];

  // Feature handlers
  const addFeature = () => setFeatures([...features, { title: '', description: '' }]);
  const removeFeature = (index: number) => setFeatures(features.filter((_, i) => i !== index));
  const updateFeature = (index: number, field: keyof Feature, value: string) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  // Benefit handlers
  const addBenefit = () => setBenefits([...benefits, { title: '', description: '' }]);
  const removeBenefit = (index: number) => setBenefits(benefits.filter((_, i) => i !== index));
  const updateBenefit = (index: number, field: keyof Benefit, value: string) => {
    const updated = [...benefits];
    updated[index] = { ...updated[index], [field]: value };
    setBenefits(updated);
  };

  // FAQ handlers
  const addFaq = () => setFaqs([...faqs, { question: '', answer: '' }]);
  const removeFaq = (index: number) => setFaqs(faqs.filter((_, i) => i !== index));
  const updateFaq = (index: number, field: keyof FAQ, value: string) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Basic info */}
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              {t.admin.editor.basicInfo}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label={`${t.admin.editor.title} *`}
                error={errors.title?.message}
                {...register('title')}
                onChange={handleTitleChange}
              />
              <Input
                label={`${t.admin.editor.slug} *`}
                error={errors.slug?.message}
                {...register('slug')}
              />
            </div>

            <div className="mt-4">
              <Textarea
                label={t.admin.editor.shortDescription}
                rows={3}
                {...register('short_description')}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-4">
              <Select
                label={t.admin.editor.icon}
                options={iconOptions}
                {...register('icon')}
              />
              <Input
                label={t.admin.editor.order}
                type="number"
                {...register('order_index', { valueAsNumber: true })}
              />
              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    {...register('is_featured')}
                  />
                  <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">
                    {t.admin.editor.featuredService}
                  </span>
                </label>
              </div>
            </div>
          </section>

          {/* Long description */}
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              {t.admin.editor.detailedDescription}
            </h3>
            <RichTextEditor
              content={longDescription}
              onChange={setLongDescription}
              placeholder={t.admin.editor.detailedDescPlaceholder}
            />
          </section>

          {/* Features */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t.admin.editor.features}
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addFeature}>
                <Plus className="w-4 h-4 mr-1" />
                {t.admin.editor.addItem}
              </Button>
            </div>
            <div className="space-y-4">
              {features.map((feature, index) => (
                <Card key={index} className="relative">
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="grid md:grid-cols-2 gap-4 pr-8">
                    <Input
                      label={t.admin.editor.featureTitle}
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    />
                    <Input
                      label={t.admin.editor.featureDescription}
                      value={feature.description}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Benefits */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t.admin.editor.benefits}
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                <Plus className="w-4 h-4 mr-1" />
                {t.admin.editor.addItem}
              </Button>
            </div>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <Card key={index} className="relative">
                  <button
                    type="button"
                    onClick={() => removeBenefit(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="grid md:grid-cols-2 gap-4 pr-8">
                    <Input
                      label={t.admin.editor.featureTitle}
                      value={benefit.title}
                      onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                    />
                    <Input
                      label={t.admin.editor.featureDescription}
                      value={benefit.description}
                      onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t.admin.editor.faq}
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addFaq}>
                <Plus className="w-4 h-4 mr-1" />
                {t.admin.editor.addItem}
              </Button>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="relative">
                  <button
                    type="button"
                    onClick={() => removeFaq(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="space-y-4 pr-8">
                    <Input
                      label={t.admin.editor.question}
                      value={faq.question}
                      onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    />
                    <Textarea
                      label={t.admin.editor.answer}
                      rows={2}
                      value={faq.answer}
                      onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              {t.admin.editor.callToAction}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Input label={t.admin.editor.ctaText} {...register('cta_text')} />
              <Input label={t.admin.editor.ctaLink} {...register('cta_link')} />
            </div>
          </section>

          {/* SEO */}
          <section className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              {t.admin.editor.seoSettings}
            </h3>
            <div className="space-y-4">
              <Input label={t.admin.editor.seoTitle} {...register('seo_title')} />
              <Textarea label={t.admin.editor.seoDescription} rows={2} {...register('seo_description')} />
              <Input label={t.admin.editor.keywords} {...register('seo_keywords')} />
              <Input label={t.admin.editor.heroImageUrl} {...register('hero_image_url')} />
            </div>
          </section>

          {/* Status */}
          <section className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <Select label={t.admin.editor.status} options={statusOptions} {...register('status')} />
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
        <Button type="button" variant="outline" onClick={onClose}>
          {t.admin.editor.cancel}
        </Button>
        <Button type="submit" isLoading={isSubmitting} leftIcon={<Save className="w-4 h-4" />}>
          {service ? t.admin.editor.saveChanges : t.admin.editor.createService}
        </Button>
      </div>
    </form>
  );
}
